import type { NormalizedCacheObject, OperationVariables, TypedDocumentNode } from "@apollo/client";
import type { GraphQLError } from "graphql";
import type { EventCallback, EventType } from "../../util/event-manager";
import type { MutationOptions } from "./mutation";
import type { QueryOptions } from "./query";
import type { GangplankClient } from "../auth/gangplank-client";
import { ApolloClient, HttpLink, InMemoryCache, from } from "@apollo/client";
import { EventManager } from "../../util/event-manager";
import { Mutation } from "./mutation";
import { TYPE_POLICIES } from "./policies";
import { Query } from "./query";


type ApiEvents = {
	unauthorized: { error: GraphQLError }
};

export namespace ApiServiceHandleErrorResult {
	export type Handled = {
		handled: true;
		retry: boolean;
	};
	export type NotFound = {
		handled: false;
		kind: "notFound";
	};
	export type Unknown = {
		handled: false;
		kind: "unknown";
	};
}
export type ApiServiceHandleErrorResult = (
	| ApiServiceHandleErrorResult.Handled
	| ApiServiceHandleErrorResult.NotFound
	| ApiServiceHandleErrorResult.Unknown
);

export type ApiServiceConfig = {
	/**
	 * URL for Mainsail API.
	 */
	uri: string;
};
export type ApiServiceDependencies = {
	gangplank: GangplankClient;
	/**
	 * Pass a mock ApolloClient implementation here for use in testing.
	 *
	 * Only used when `NODE_ENV` === 'testing', when it is required.
	 */
	mockClient?: ApolloClient<NormalizedCacheObject>;
};
/**
 * Manages our interactions with the Mainsail API.
 */
export class ApiService {
	readonly #gangplank: GangplankClient;
	readonly #client: ApolloClient<NormalizedCacheObject>;
	readonly #events = new EventManager<ApiEvents>();

	#activeReset: Promise<unknown> | null = null;

	constructor(config: Readonly<ApiServiceConfig>, deps: Readonly<ApiServiceDependencies>) {
		this.#gangplank = deps.gangplank;
		this.#client = this.#makeClient(config, deps);
	}

	#makeClient(config: Readonly<ApiServiceConfig>, deps?: Readonly<ApiServiceDependencies>): ApolloClient<NormalizedCacheObject> {
		if(process.env["NODE_ENV"] === "testing") {
			if(!deps?.mockClient) {
				throw new Error("No mockClient set in dependencies.");
			}

			return deps.mockClient;
		}

		return new ApolloClient({
			link: from([
				new HttpLink({
					uri: config.uri,
					credentials: "include",
				}),
			]),
			cache: new InMemoryCache({
				canonizeResults: true,
				typePolicies: TYPE_POLICIES,
			}),
			assumeImmutableResults: true,
			connectToDevTools: true,
		});
	}

	/**
	 * Called by `Query` & `Mutation` when encountering GraphQL errors.
	 */
	private async handleError(error: GraphQLError): Promise<ApiServiceHandleErrorResult> {
		switch(error.extensions?.["code"]) {
			case "UNAUTHORIZED":
				if(error.extensions["expired"]) {
					const refreshed = await this.#gangplank.refresh();
					if(refreshed) {
						return {
							handled: true,
							retry: true,
						};
					}
				}
				this.#events.notify("unauthorized", { error });
				return {
					handled: true,
					retry: false,
				};
			case "NOT_FOUND":
				return {
					handled: false,
					kind: "notFound",
				};
			default:
				return {
					handled: false,
					kind: "unknown",
				};
		}
	}
	/**
	 * Create a watched query.
	 * @returns
	 */
	query<Data, Variables extends OperationVariables>(query: TypedDocumentNode<Data, Variables>, initialVariables?: Variables, options?: Omit<QueryOptions<Data, Variables>, "query" | "variables">): Query<Data, Variables> {
		return new Query(this, this.#client, { query, variables: initialVariables, ...options });
	}

	queryOnce<Data, Variables extends OperationVariables>(query: TypedDocumentNode<Data, Variables>, variables?: Variables, options?: Omit<QueryOptions<Data, Variables>, "query" | "variables">): Promise<Data> {
		return new Promise((resolve, reject) => {
			this.query(query, variables, options)
				.on("data", ev => {
					ev.query.dispose();
					resolve(ev.data as Data);
				})
				.on("notFound", ev => {
					ev.query.dispose();
					reject(ev.error);
				})
				.on("error", ev => {
					reject(ev.error);
				});
		});
	}

	mutation<Data, Variables extends OperationVariables>(mutation: TypedDocumentNode<Data, Variables>, options?: Omit<MutationOptions<Data, Variables>, "mutation">): Mutation<Data, Variables> {
		return new Mutation(this, this.#client, { mutation, ...options });
	}

	async mutateOnce<Data, Variables extends OperationVariables>(mutation: TypedDocumentNode<Data, Variables>, variables: Variables, options?: Omit<MutationOptions<Data, Variables>, "mutation">): Promise<Data> {
		const mut = this.mutation(mutation, options);
		try {
			return mut.submitOrThrow(variables);
		} finally {
			mut.dispose();
		}
	}

	/**
	 * Reset the cache & re-fetch all active {@link Query}s.
	 * @returns
	 */
	async reset(): Promise<void> {
		/*
			If we run multiple resets in quick succession, eg for a failed masquerade
			attempt, we can wind up with stale in-flight queries raising errors.

			So if we are already resetting the store, wait for that reset to complete,
			and then reset the store again.
		*/
		if(this.#activeReset) {
			await this.#activeReset;
		}

		return this.#activeReset = this.#reset();
	}

	async #reset(): Promise<void> {
		try {
			await this.#client.resetStore();
		} finally {
			this.#activeReset = null;
		}
	}

	on<E extends EventType<ApiEvents>>(event: E, listener: EventCallback<ApiEvents, E>): void {
		this.#events.on(event, listener);
	}

	off<E extends EventType<ApiEvents>>(event: E, listener: EventCallback<ApiEvents, E>): void {
		this.#events.off(event, listener);
	}

	/**
	 * Used by `Mutation` & `Query` to make their requests.
	 */
	private get client(): ApolloClient<NormalizedCacheObject> {
		return this.#client;
	}
}
